import { useId, useMemo, useState } from "react";
import { scaleLinear, scaleTime } from "d3-scale";
import { line, area, curveMonotoneX } from "d3-shape";
import { extent, max, bisector } from "d3-array";
import { timeFormat } from "d3-time-format";
import { useResize } from "./useResize";
import { ChartTooltip } from "./Tooltip";
import { CHART_TOKENS, seriesColor, fmtInt } from "./theme";
import { useTranslation } from "../../i18n/LocaleProvider";

// Parses a data point's date into a JS Date so charts accept either strings
// (ISO) or already-parsed Dates transparently.
const toDate = (d) => (d instanceof Date ? d : new Date(d));
const fmtShort = timeFormat("%b %d");
const fmtFull = timeFormat("%b %d, %Y");

// Simple, opinionated line chart with a gradient area fill. Callers pass
// `data` as `[{date, value}]` for a single series or an array of those for
// multiple series. Everything else has a sane default.
export const LineChart = ({
  data,
  height = 220,
  yLabel,
  color,
  seriesNames,
  minY = 0,
  valueFormatter = fmtInt,
  dateFormatter,
}) => {
  const { t } = useTranslation();
  const [ref, { width }] = useResize(320, height);
  const [hover, setHover] = useState(null);

  // Normalise to multi-series shape. Any single-series input becomes a
  // one-element list so the render path branches in one spot.
  const seriesList = useMemo(() => {
    if (!data || data.length === 0) return [];
    const isNested = Array.isArray(data[0]);
    return isNested ? data : [data];
  }, [data]);

  const PAD = { top: 12, right: 16, bottom: 28, left: 44 };
  const plotW = Math.max(10, width - PAD.left - PAD.right);
  const plotH = Math.max(10, height - PAD.top - PAD.bottom);

  const { xScale, yScale, allPoints } = useMemo(() => {
    const flat = seriesList.flat();
    if (flat.length === 0) return { xScale: null, yScale: null, allPoints: [] };
    const xs = flat.map((d) => toDate(d.date));
    const ymax = max(flat, (d) => d.value) ?? 1;
    return {
      xScale: scaleTime().domain(extent(xs)).range([0, plotW]),
      yScale: scaleLinear()
        .domain([minY, ymax * 1.15 || 1])
        .nice()
        .range([plotH, 0]),
      allPoints: flat,
    };
  }, [seriesList, plotW, plotH, minY]);

  const isEmpty = seriesList.length === 0 || allPoints.length === 0;

  const tickLabelFmt = dateFormatter || fmtShort;
  const yTicks = yScale ? yScale.ticks(5) : [];
  const xTicks = xScale ? xScale.ticks(Math.min(6, Math.max(2, Math.floor(plotW / 80)))) : [];

  const lineGen =
    xScale && yScale
      ? line()
          .x((d) => xScale(toDate(d.date)))
          .y((d) => yScale(d.value))
          .curve(curveMonotoneX)
      : null;
  const areaGen =
    xScale && yScale
      ? area()
          .x((d) => xScale(toDate(d.date)))
          .y0(plotH)
          .y1((d) => yScale(d.value))
          .curve(curveMonotoneX)
      : null;

  // On hover, snap to the closest date across all flat points so multi-series
  // tooltips align on the same timestamp.
  const datesSorted = useMemo(() => {
    if (!xScale) return [];
    const set = new Map();
    for (const d of allPoints) {
      const k = +toDate(d.date);
      if (!set.has(k)) set.set(k, toDate(d.date));
    }
    return [...set.values()].sort((a, b) => a - b);
  }, [allPoints, xScale]);
  const bisect = useMemo(() => bisector((d) => d).left, []);

  const onMove = (e) => {
    if (!xScale || datesSorted.length === 0) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const mx = e.clientX - rect.left - PAD.left;
    const xVal = xScale.invert(mx);
    const i = bisect(datesSorted, xVal);
    const d0 = datesSorted[i - 1];
    const d1 = datesSorted[i];
    const snap = !d0 ? d1 : !d1 ? d0 : xVal - d0 > d1 - xVal ? d1 : d0;
    if (!snap) return;
    const rows = seriesList.map((series, si) => {
      const hit = series.find((p) => +toDate(p.date) === +snap);
      return {
        name: seriesNames?.[si] ?? `Series ${si + 1}`,
        value: hit?.value ?? 0,
        color: color && seriesList.length === 1 ? color : seriesColor(si),
      };
    });
    setHover({ date: snap, rows, x: xScale(snap) + PAD.left, y: PAD.top + plotH / 2 });
  };

  return (
    <div ref={ref} style={{ position: "relative", width: "100%", height }}>
      {isEmpty ? (
        <div style={emptyStyle}>{t("components.charts.line_chart.no_data_for_this_range")}</div>
      ) : (
        <svg width={width} height={height} onMouseMove={onMove} onMouseLeave={() => setHover(null)}>
          <defs>
            {seriesList.map((_, i) => {
              const c = color && seriesList.length === 1 ? color : seriesColor(i);
              return (
                <linearGradient key={i} id={`lc-grad-${i}`} x1="0" x2="0" y1="0" y2="1">
                  <stop offset="0%" stopColor={c} stopOpacity={0.22} />
                  <stop offset="100%" stopColor={c} stopOpacity={0} />
                </linearGradient>
              );
            })}
          </defs>

          {/* gridlines */}
          <g transform={`translate(${PAD.left},${PAD.top})`}>
            {yTicks.map((t) => (
              <g key={t}>
                <line x1={0} x2={plotW} y1={yScale(t)} y2={yScale(t)} stroke={CHART_TOKENS.grid} />
                <text
                  x={-8}
                  y={yScale(t)}
                  textAnchor="end"
                  dominantBaseline="middle"
                  fontSize={11}
                  fill={CHART_TOKENS.label}
                >
                  {valueFormatter(t)}
                </text>
              </g>
            ))}
            {xTicks.map((t) => (
              <text key={+t} x={xScale(t)} y={plotH + 18} textAnchor="middle" fontSize={11} fill={CHART_TOKENS.label}>
                {tickLabelFmt(t)}
              </text>
            ))}

            {/* area fill + line per series */}
            {seriesList.map((series, i) => {
              const c = color && seriesList.length === 1 ? color : seriesColor(i);
              return (
                <g key={i}>
                  <path d={areaGen(series)} fill={`url(#lc-grad-${i})`} />
                  <path
                    d={lineGen(series)}
                    fill="none"
                    stroke={c}
                    strokeWidth={2}
                    strokeLinejoin="round"
                    strokeLinecap="round"
                  />
                </g>
              );
            })}

            {/* hover guideline */}
            {hover && (
              <g>
                <line
                  x1={hover.x - PAD.left}
                  x2={hover.x - PAD.left}
                  y1={0}
                  y2={plotH}
                  stroke={CHART_TOKENS.axis}
                  strokeDasharray="2 3"
                />
                {seriesList.map((series, i) => {
                  const hit = series.find((p) => +toDate(p.date) === +hover.date);
                  if (!hit) return null;
                  const c = color && seriesList.length === 1 ? color : seriesColor(i);
                  return (
                    <circle
                      key={i}
                      cx={xScale(toDate(hit.date))}
                      cy={yScale(hit.value)}
                      r={4}
                      fill="#fff"
                      stroke={c}
                      strokeWidth={2}
                    />
                  );
                })}
              </g>
            )}

            {yLabel && (
              <text
                transform={`translate(${-36},${plotH / 2}) rotate(-90)`}
                textAnchor="middle"
                fontSize={11}
                fill={CHART_TOKENS.label}
              >
                {yLabel}
              </text>
            )}
          </g>
        </svg>
      )}

      <ChartTooltip visible={!!hover} x={hover?.x ?? 0} y={hover?.y ?? 0}>
        {hover && (
          <>
            <div style={{ opacity: 0.75, marginBottom: 4 }}>{fmtFull(hover.date)}</div>
            {hover.rows.map((r) => (
              <div key={r.name} style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <span style={{ width: 8, height: 8, borderRadius: 2, background: r.color, display: "inline-block" }} />
                <span style={{ opacity: 0.85 }}>{r.name}</span>
                <span style={{ marginLeft: "auto", fontWeight: 600 }}>{valueFormatter(r.value)}</span>
              </div>
            ))}
          </>
        )}
      </ChartTooltip>
    </div>
  );
};

const emptyStyle = {
  position: "absolute",
  inset: 0,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  color: CHART_TOKENS.label,
  fontSize: "var(--font-size-label)",
};
